import { extractFirstStackFrame } from '../../../../submodules/web-shared/elements/ErrorBoundary/CrashError';
import { ddLog, pushLog } from './log-store.js';

const NOISE_CHARS_RE = /[\u200B-\u200D\uFEFF\u00A0]/g;

// Twilio in-app calls produce a known, high-volume set of errors the call hooks already handle (token
// refresh, transport reconnect, user-facing snackbars). They share one fingerprint so the whole group
// can be ignored once in Error Tracking. Any Twilio error outside this list keeps the default
// fingerprint and stays visible as its own issue, so new failures are still tracked.
const KNOWN_TWILIO_ERROR_FINGERPRINT = 'known_twilio_error_v2';
// The grouped messages have no common shape, so Error Tracking titles the issue with a fully masked
// pattern. A constant prefix gives the issue a readable title while the original message is kept
// intact for the session replay timeline.
const KNOWN_TWILIO_ERROR_PREFIX = 'Known Twilio error: ';

// Errors the in-app calls hooks report through datadogRum.addError after handling them.
const KNOWN_TWILIO_CUSTOM_ERROR_MESSAGES = ['Twilio error', 'Twilio device connect error'];

const KNOWN_TWILIO_ERROR_SIGNATURES = [
  'AccessTokenInvalid (20101)',
  'AccessTokenExpired (20104)',
  'UnknownError (31000)', // considered as known because it is a generic error that can't be categorized by twilio. f.e Public Switched Telephone Network (PSTN) errors
  'ConnectionError (31005)',
  'TransportError (31009)',
  'PermissionDeniedError (31401)',
  'AcquisitionFailedError (31402)',
];
// Every Voice SDK console log carries this tag, e.g. "[TwilioVoice][WSTransport] WebSocket received error: ...".
const TWILIO_SDK_LOG_PREFIX = '[TwilioVoice]';
// Signaling and telemetry noise the Voice SDK logs without an error type.
const KNOWN_TWILIO_LOG_MARKERS = [
  'Received websocket close event',
  'WebSocket received error',
  'Received transportClose from pstream',
  'event to Insights',
];

function isKnownTwilioError(message) {
  if (KNOWN_TWILIO_CUSTOM_ERROR_MESSAGES.includes(message)) return true;

  if (KNOWN_TWILIO_ERROR_SIGNATURES.some(signature => message.includes(signature))) return true;

  return message.includes(TWILIO_SDK_LOG_PREFIX)
    && KNOWN_TWILIO_LOG_MARKERS.some(marker => message.includes(marker));
}

// Builds a persistent fingerprint from the error type, message, and first call-site name.
function computeFingerprint(event) {
  const type = event.error?.type || 'Error';
  const message = (event.error?.message && typeof event.error.message === 'string')
    ? event.error.message : '';

  if (message.startsWith('csp_violation:')) {
    return 'csp_violation';
  }

  if (message.includes('Failed to initialize WebGL')) {
    return 'webgl_initialization';
  }

  if (message.includes('Unexpected string response')) {
    return 'response_decoding_error';
  }

  if (message.includes('UNLEASH_FLAGS_FETCH_ERROR')) {
    return 'unleash_flags_fetch_error';
  }

  if (isKnownTwilioError(message)) {
    return KNOWN_TWILIO_ERROR_FINGERPRINT;
  }

  const firstFrame = extractFirstStackFrame(event.error?.stack);
  return `${type}:${message}:${firstFrame}`;
}

function normalizeLabel(raw = '') {
  return raw.replace(NOISE_CHARS_RE, ' ').replace(/\s+/g, ' ').trim();
}

function extractTestId(selector = '') {
  const m = selector.match(/\[data-test(?:id|-id)="([^"]+)"\]/);
  return m ? m[1] : null;
}

function handleAction(event, t) {
  if (event.action.type === 'click') {
    const selector = event._dd?.action?.target?.selector || '';
    const name = event.action.target?.name;
    if (selector.includes('__dd-qa-root')) return;
    // DD marks clicks with no DOM/network effect as dead_click — skip them (container/background clicks)
    if (event.action.frustration?.type?.includes('dead_click')) return;
    const label = normalizeLabel(name || '?');
    const testId = extractTestId(selector);
    pushLog({ t, type: 'click', label: `Clicked [${label}]`, raw: label, testId });
  } else if (event.action.type === 'custom') {
    const label = event.action.target?.name || '?';
    const value = event.context?.value;
    pushLog({ t, type: 'input', label: value ? `${label} → "${value}"` : label, field: event.context?.field, value });
  }
}

const isErrorIgnored = (errorMessage) => {
  if (typeof errorMessage !== 'string') return false;

  const ignoredErrors = [
    'Unleash:',
  ];

  return ignoredErrors.some(ignored => errorMessage.startsWith(ignored));

};

export function createBeforeSend({ onError } = {}) {
  let lastViewId = null;

  function handleView(event, t) {
    const viewId = event.view?.id;
    if (viewId && viewId === lastViewId) return;
    lastViewId = viewId || null;

    if (event.view?.loading_type === 'initial_load') return; // pushed manually in index.js after initLogSession

    const lastNav = [...ddLog].reverse().find(e => e.type === 'nav');
    if (lastNav?.url === event.view.url) return;
    pushLog({ t, type: 'nav', label: `Navigated to "${event.view.url}"`, url: event.view.url });
  }

  return (event, _context) => {
    const t = new Date(event.date).toISOString();

    if (event.type === 'action' && onError) {
      handleAction(event, t);
      return true;
    }

    if (event.type === 'error') {
      const message = event.error?.message || 'Unknown error';
      const errorType = event.error?.type || '';
      const source = event.error?.source || '';

      if (isErrorIgnored(message)) {
        return false;
      }

      const fingerprint = computeFingerprint(event);
      const isKnownTwilioNoise = fingerprint === KNOWN_TWILIO_ERROR_FINGERPRINT;

      if (event.error) {
        event.error.fingerprint = fingerprint;

        if (isKnownTwilioNoise) {
          event.error.message = `${KNOWN_TWILIO_ERROR_PREFIX}${message}`;
        }
      }

      if (!isKnownTwilioNoise && (source === 'source' || source === 'console')) {
        onError?.({ message, errorType, source, t });
      }
      return true;
    }

    if (event.type === 'view' && onError) {
      handleView(event, t);
    }

    return true;
  };
}